import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CopyPlus, ExternalLink, Loader2, MoreHorizontal, Trash2, FolderOpen, Info, Pencil } from "lucide-react";
import LiveThumb from "./LiveThumb";
import { timeAgo } from "../../lib/streamParse";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";

const Pill = ({ tone, children, testid }) => (
  <span className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-lg text-[13px] font-medium ${tone}`} data-testid={testid}>{children}</span>
);

const RowMenu = ({ project, onDuplicate, onDelete, onRename }) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`project-menu-${project.id}`} aria-label="Project actions" className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
          <MoreHorizontal size={20} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 rounded-xl bg-white border-slate-200 p-1">
        <DropdownMenuItem onSelect={() => navigate(`/project/${project.id}`)} className="gap-2 rounded-lg cursor-pointer" data-testid={`project-open-${project.id}`}><FolderOpen size={14} /> Open</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onRename(project)} className="gap-2 rounded-lg cursor-pointer" data-testid={`project-rename-${project.id}`}><Pencil size={14} /> Rename</DropdownMenuItem>
        {project.slug && project.published_version_id && (
          <DropdownMenuItem onSelect={() => window.open(`/p/${project.slug}`, "_blank")} className="gap-2 rounded-lg cursor-pointer" data-testid={`project-visit-${project.id}`}><ExternalLink size={14} /> Visit live app</DropdownMenuItem>
        )}
        {project.current_version_id && (
          <DropdownMenuItem onSelect={() => onDuplicate(project)} className="gap-2 rounded-lg cursor-pointer" data-testid={`project-duplicate-${project.id}`}><CopyPlus size={14} /> Duplicate</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onDelete(project)} className="gap-2 rounded-lg cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50" data-testid={`project-delete-${project.id}`}><Trash2 size={14} /> Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ProjectRow = ({ project, onDelete, onDuplicate, onRename }) => {
  const published = !!project.published_version_id;
  const building = project.status === "generating";
  const draftAhead = published && project.published_version_id !== project.current_version_id;
  return (
    <Link to={`/project/${project.id}`} data-testid={`project-card-${project.id}`}
      className="group flex items-center gap-4 sm:gap-6 py-5 border-b border-slate-200/80 last:border-0">
      <div className="shrink-0 w-32 h-20 sm:w-44 sm:h-28 rounded-2xl p-1.5 bg-white ring-1 ring-slate-200/80 shadow-[0_6px_20px_-10px_rgba(15,23,42,0.25)] group-hover:shadow-[0_10px_28px_-10px_rgba(14,165,233,0.35)] transition-shadow">
        <div className="w-full h-full rounded-xl overflow-hidden"><LiveThumb versionId={project.current_version_id} name={project.name} /></div>
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-900 truncate group-hover:text-sky-700 transition-colors" data-testid="project-card-name">{project.name}</h3>
          <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}><RowMenu project={project} onDuplicate={onDuplicate} onDelete={onDelete} onRename={onRename} /></div>
        </div>
        <p className="text-[15px] text-slate-500">Updated {timeAgo(project.updated_at)}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {building && <Pill tone="bg-amber-50 text-amber-700" testid="project-building-badge"><Loader2 size={12} className="animate-spin" /> Building</Pill>}
          {published && <Pill tone="bg-emerald-50 text-emerald-700" testid="project-published-badge">Published</Pill>}
          {draftAhead && <Pill tone="bg-yellow-100 text-yellow-800" testid="project-unpublished-badge">Unpublished changes <Info size={13} /></Pill>}
        </div>
      </div>
    </Link>
  );
};

export default ProjectRow;
